import { useEffect } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { RadioButton } from 'primereact/radiobutton'
import FormDialog from '../../../components/ui/FormDialog'
import FormDateField from '../../../components/ui/form/FormDateField'
import FormDropdownField from '../../../components/ui/form/FormDropdownField'
import FormTextField from '../../../components/ui/form/FormTextField'
import FormTextareaField from '../../../components/ui/form/FormTextareaField'
import FormTimeField from '../../../components/ui/form/FormTimeField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { formatDuration, shortTime } from '../duration'
import { meetingRecordFormSchema, type MeetingRecordFormValues } from './meetingRecordFormSchema'
import type { MeetingRecord, MeetingRecordUpsert, TimePortfolioLookups } from '../types'

interface Props {
  visible: boolean
  initial: MeetingRecord | null
  lookups: TimePortfolioLookups | undefined
  submitting: boolean
  onSubmit: (payload: MeetingRecordUpsert) => void
  onHide: () => void
}

/** Boş form değerleri — hem açılışta hem "yeni kayıt"ta aynı yerden gelir. */
const EMPTY: MeetingRecordFormValues = {
  meetingOrActivityId: null,
  meetingTypeId: null,
  name: '',
  recordDate: '',
  startTime: '',
  finishTime: '',
  authorityMandatory: false,
  description: '',
}

/**
 * Toplantı Kaydı ekle/düzenle formu (react-hook-form + zod).
 *
 * <p>**Süre yazılmaz, gösterilir**: form saat aralığından anlık hesaplar ve salt-okunur bir satır
 * olarak çizer — veritabanında süre kolonu yoktur ve kullanıcı onu değiştiremez (analistin alan
 * listesi: "Bitiş − Başlangıç şeklinde otomatik hesaplanır. Kullanıcı değiştiremez.").
 *
 * <p>"Otorite Zorunlu" iki radyo düğmesidir (varsayılan **Hayır**) — analistin alan listesi radio
 * group diyor. Etiketler kutuların **kardeşidir** (`admin-form-check`), iç içe interaktif kontrol
 * yoktur.
 */
export default function MeetingRecordFormDialog({
  visible,
  initial,
  lookups,
  submitting,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, watch } = useZodForm(meetingRecordFormSchema, {
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (!visible) return
    reset(
      initial
        ? {
            meetingOrActivityId: initial.meetingOrActivityId,
            meetingTypeId: initial.meetingTypeId,
            name: initial.name,
            recordDate: initial.recordDate,
            startTime: shortTime(initial.startTime),
            finishTime: shortTime(initial.finishTime),
            authorityMandatory: initial.authorityMandatory,
            description: initial.description ?? '',
          }
        : EMPTY,
    )
  }, [visible, initial, reset])

  const startTime = watch('startTime')
  const finishTime = watch('finishTime')
  const durationLabel = formatDuration(minutesBetween(startTime, finishTime), {
    hour: t('timePortfolio.duration.hour'),
    minute: t('timePortfolio.duration.minute'),
  })

  // `requiredId()` tipi `number | null` birakir (bos varsayilan atanabilsin diye); sema dogrulamasi
  // null'i zaten reddettigi icin buraya yalnizca dolu deger ulasir.
  const onValid = (v: MeetingRecordFormValues) =>
    onSubmit({
      meetingOrActivityId: v.meetingOrActivityId as number,
      meetingTypeId: v.meetingTypeId as number,
      name: v.name,
      recordDate: v.recordDate,
      startTime: v.startTime,
      finishTime: v.finishTime,
      authorityMandatory: v.authorityMandatory,
      description: v.description ? v.description : null,
    })

  return (
    <FormDialog
      visible={visible}
      header={
        initial ? t('timePortfolio.meeting.form.edit') : t('timePortfolio.meeting.form.new')
      }
      submitLabel={t('timePortfolio.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormDropdownField
          control={control}
          name="meetingOrActivityId"
          label={t('timePortfolio.meeting.columns.kind')}
          options={(lookups?.kinds ?? []).map((k) => ({ label: k.name, value: k.id }))}
          required
        />
        <FormTextField
          control={control}
          name="name"
          label={t('timePortfolio.meeting.columns.name')}
          required
        />
        <FormDateField
          control={control}
          name="recordDate"
          label={t('timePortfolio.meeting.columns.date')}
          required
        />
        {/* Iki saat YAN YANA ama ETIKETLERI AYRI (kullanici karari 23.09.2026). Paylasilan
            `FormTimeRangeField` de yan yana cizer, ancak tek bir ortak baslik ("Saat Araligi") kullanip
            alan adlarini yer tutucuya dusurur; analistin alan listesi "Baslangic Saati" ve "Bitis Saati"
            diye iki ayri etiket istiyor. Bu yuzden iki `FormTimeField` bir satir sarmalayicisina konur -
            duzen CSS'te, alanlarin kendisi yine paylasilan bilesendir. */}
        <div className="tp-time-row">
          {/* `fillOnOpen`: panel acilir acilmaz alan dolar. PrimeReact timeOnly paneli bir saat
              GOSTERIR ama yazmaz; kullanici ok tuslariyla degistirmeden alan bos kalirdi ("gordugum
              saati onayla" diyecek bir dugme yok). Baslangic simdiki saate, bitis ise baslangic +
              1 saate duser - sifir sureli bir kayit dogrudan gecersiz olurdu. */}
          <FormTimeField
            control={control}
            name="startTime"
            label={t('timePortfolio.meeting.columns.start')}
            required
            fillOnOpen={nowTime}
          />
          <FormTimeField
            control={control}
            name="finishTime"
            label={t('timePortfolio.meeting.columns.finish')}
            required
            fillOnOpen={() => addHour(startTime) ?? nowTime()}
          />
        </div>
        {/* Salt-okunur sure: etiketi ve degeri diger alanlarla AYNI olculerde cizilir — paylasilan
            `ui-field`/`ui-field-label` sinifi kullanilir, yoksa dialogun varsayilan yazi boyutunu
            alip komsu etiketlerden buyuk gorunuyordu. */}
        <div className="ui-field">
          <span className="ui-field-label">{t('timePortfolio.meeting.columns.duration')}</span>
          <output className="tp-duration" aria-live="polite">
            {durationLabel}
          </output>
        </div>
        <FormDropdownField
          control={control}
          name="meetingTypeId"
          label={t('timePortfolio.meeting.columns.category')}
          options={(lookups?.categories ?? []).map((c) => ({ label: c.name, value: c.id }))}
          required
        />
        <Controller
          control={control}
          name="authorityMandatory"
          render={({ field }) => (
            <div className="ui-field">
              <span className="ui-field-label">
                {t('timePortfolio.meeting.columns.authority')}
              </span>
              <div className="tp-radio-row">
                <span className="admin-form-check">
                  <RadioButton
                    inputId="authority-no"
                    name="authorityMandatory"
                    checked={!field.value}
                    onChange={() => field.onChange(false)}
                  />
                  <label htmlFor="authority-no">{t('timePortfolio.authority.no')}</label>
                </span>
                <span className="admin-form-check">
                  <RadioButton
                    inputId="authority-yes"
                    name="authorityMandatory"
                    checked={field.value}
                    onChange={() => field.onChange(true)}
                  />
                  <label htmlFor="authority-yes">{t('timePortfolio.authority.yes')}</label>
                </span>
              </div>
            </div>
          )}
        />
        <FormTextareaField
          control={control}
          name="description"
          label={t('timePortfolio.meeting.columns.description')}
          full
        />
      </div>
    </FormDialog>
  )
}

/** Şu anki saat "HH:mm" (24 saat, sıfır dolgulu). */
function nowTime(): string {
  const d = new Date()
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/**
 * "HH:mm" + 1 saat; **gün taşarsa 23:59'da durur**, geçersizse `null`.
 *
 * Kırpma bilinçlidir ve `FormTimeRangeField`'ın aynı amaçlı yardımcısından **bilerek ayrılır**: orası
 * doğal taşmayı yapar (23:30 → 00:30). Burada o değer **kuralı kırardı** — şema bitişi başlangıçla
 * sözlüksel (metin) olarak karşılaştırıyor, yani "00:30" < "23:30" görünür ve kullanıcı daha formu
 * doldurmadan hata alırdı. Kayıt zaten aynı güne yazılıyor (gece yarısını aşan toplantı kapsam dışı).
 */
function addHour(value: string): string | null {
  if (!value) return null
  const [h, m] = value.split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return null
  return h >= 23 ? '23:59' : `${String(h + 1).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** "HH:mm" ikilisinden dakika farkı; biri eksikse `null` (süre hücresi "-" gösterir). */
function minutesBetween(start: string, finish: string): number | null {
  if (!start || !finish) return null
  const [sh, sm] = start.split(':').map(Number)
  const [fh, fm] = finish.split(':').map(Number)
  if ([sh, sm, fh, fm].some(Number.isNaN)) return null
  return fh * 60 + fm - (sh * 60 + sm)
}
